import React, { useEffect } from "react";
import {
	DataPayload,
	FetchDataFailureAction,
	FetchDataSuccessAction,
	fetchData,
	patchData,
} from "../../store/actions";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { setIsLoading } from "../../store/LoadingSlice";
import { RootState } from "../../store/Store";
import { ViewUser } from "../../Service/Apis";

import { useDispatch, useSelector } from "react-redux";
import {
	setIsLoggedIn,
	setRoles,
	setSelectedRole,
	setUserId,
} from "./../../store/AuthSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { getSidebarItems } from "../Data/data";
import axios from "axios";

interface LayoutProps {
	children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const sidebarItems = useSelector((state: any) => state.navbar.sidebarItems);
	const isLoggedIn = useSelector((state: any) => state.auth.isLoggedIn);
	const dispatch2: ThunkDispatch<
		RootState,
		null,
		FetchDataSuccessAction | FetchDataFailureAction
	> = useDispatch();
	const email = localStorage.getItem("email");

	const roles = useSelector((state: RootState) => state.auth.roles);
	const selectedRole = useSelector((state: RootState) => state.auth.selectedRole);

	
	useEffect(() => {
		const token = localStorage.getItem("token");
		const isAuthenticated = token !== null;
		dispatch(setIsLoggedIn(isAuthenticated));

		const fetchUserData = async () => {
			try {
				if (!token) return;

				dispatch(setIsLoading(true));
				const response = await dispatch2(fetchData(`${ViewUser}${email}`));
				const userData = response; // Extract the user data payload
				// Set userId in Redux from the fetched user data
				if (userData && userData[0]?.id) {
					dispatch(setUserId(userData[0].id));
				}

				dispatch(setRoles(userData[0]?.roles)); // Dispatch the roles to Redux

				dispatch(setIsLoading(false));
			} catch (error) {
				console.error("Error fetching user data:", error);
				dispatch(setIsLoading(false));
			}
		};

		// Only the public customer-facing form (/feedback/<uuid>) is truly public.
		// Internal management pages (/feedback/templates, /feedback/requests, etc.)
		// must still call fetchUserData so that roles load and the navbar works.
		const isPublicFeedbackPath =
			location.pathname.startsWith("/feedback/") &&
			!location.pathname.startsWith("/feedback/templates") &&
			!location.pathname.startsWith("/feedback/requests");
		const isPublicPath =
			location.pathname === "/login" ||
			location.pathname === "/forgotpassword" ||
			isPublicFeedbackPath;

		if (!isAuthenticated && !email) {
			if (!isPublicPath) {
				navigate("/login");
			}
		} else if (!isPublicFeedbackPath) {
			// Skip fetchUserData on public feedback pages — it runs with the
			// logged-in token but the page doesn't need auth context, and a
			// 401 here used to redirect to the wrong URL.
			fetchUserData();
		}
	}, [dispatch, navigate, location]);

	const checkAuthorization = (roleArray: string[], currentSelectedRole: string): boolean => {
		const selectedRoleLower = currentSelectedRole.toLowerCase();
		// the sidebar shows, and avoids label-dedup loss with multi-role users.
		const authorizedItems = getSidebarItems([selectedRoleLower]);

		const currentRoute = window.location.pathname.replace("/", "");

		const getAllRoutes = (items: any[]): string[] => {
			const routes: string[] = [];
			items.forEach((item: any) => {
				routes.push(item.route);
				if (item.children) {
					routes.push(...getAllRoutes(item.children));
				}
			});
			return routes;
		};

		const allAuthorizedRoutes = getAllRoutes(authorizedItems);

		const isAuthorized =
			currentRoute === "dashboard" ||
			currentRoute === "profile" ||
			currentRoute === "viewform" ||
			currentRoute === "fillForm" ||
			currentRoute === "managerreview" ||
			currentRoute === "reviewForm" ||
			currentRoute === "logout" ||
			currentRoute === "editform" ||
			currentRoute === "showresponse" ||
			currentRoute === "showspecificresponse" ||
			currentRoute === "showspecificresponsehr" ||
			currentRoute === "managerreview" ||
			currentRoute === "download" ||
			currentRoute === "downloadadmin" ||
			currentRoute === "employeepreviousresponse" ||
			currentRoute === "addEmployee" ||
			currentRoute === "addemployee" ||
			currentRoute === "viewEmployee" ||
			currentRoute === "createteam" ||
			currentRoute === "addTeam" ||
			currentRoute === "editTeam" ||
			currentRoute === "viewTeam" ||
			currentRoute === "forgotpassword" ||
			currentRoute === "showthreesixtyformresponse" ||
			currentRoute === "employee360previousresponse" ||
			currentRoute === "addoreditskills" ||
			currentRoute === "showfeedbackresponse" ||
			currentRoute === "previousresponses" ||
			currentRoute === "reviews" ||
			currentRoute === "final" ||
			currentRoute === "skill-document" ||
			currentRoute === "attendance" ||
			// currentRoute === "datapoints" ||
			currentRoute === "utilization" ||
			currentRoute === "skill-based-employee" ||
			currentRoute === "historical-appraisal" ||
			currentRoute === "process-compliance-templates" ||
			currentRoute === "process-compliance-assignments" ||
			currentRoute === "compliance-dashboard" ||
			currentRoute === "compliance-templates" ||
			currentRoute.startsWith("process-compliance-form/") ||
			currentRoute.startsWith("assign-manager/") ||
			allAuthorizedRoutes.some((route: string) =>
				currentRoute === route || currentRoute.startsWith(route + "/")
			);

		// PC Manager is intentionally blocked from the full compliance dashboard.
		// Check against selectedRole so Admin+PCM multi-role users are not affected
		// when acting as Admin.
		const isPCManager = selectedRoleLower === "process compliance manager";
		if (isPCManager && currentRoute === "compliance-dashboard") {
			return false;
		}

		return isAuthorized;
	};
	// current path. Runs on every navigation and after a role switch.
	useEffect(() => {
		const isPublicPath =
			location.pathname === "/login" ||
			location.pathname === "/forgotpassword" ||
			(location.pathname.startsWith("/feedback/") &&
				!location.pathname.startsWith("/feedback/templates") &&
				!location.pathname.startsWith("/feedback/requests"));

		if (!isPublicPath && roles && roles.length > 0 && selectedRole) {
			if (!checkAuthorization(roles, selectedRole)) {
				navigate("/landing");
			}
		}
	}, [location.pathname, selectedRole, roles]);

	const handleUnauthorizedError = () => {
		navigate("/login");
	};

	useEffect(() => {
		const axiosInstance = axios.create();

		// Register the response error handler interceptor
		const errorInterceptor = axiosInstance.interceptors.response.use(
			(response) => response,
			(error) => {
				if (error.response?.status === 401) {
					handleUnauthorizedError();
				}
				return Promise.reject(error);
			}
		);

		return () => {
			// Cleanup the interceptor when the component unmounts
			axiosInstance.interceptors.response.eject(errorInterceptor);
		};
	}, []);

	return <>{children}</>;
};

export default Layout;
